/**
 * Settings Store (Zustand)
 * Manages user preferences with localStorage persistence
 *
 * State includes:
 * - Auto-save settings
 * - Execution polling interval
 * - Notification preferences
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

// ============================================================================
// INTERFACES
// ============================================================================

export interface WorkflowSettings {
  /** Enable automatic saving of workflows */
  autoSave: boolean;

  /** Auto-save interval in seconds */
  autoSaveInterval: number;

  /** Execution status polling interval in milliseconds */
  executionPollingInterval: number;

  /** Show execution completion notifications */
  showExecutionNotifications: boolean;

  /** Node execution timeout in seconds (0 = no timeout) */
  nodeExecutionTimeout: number;

  /** Enable automatic retries for failed nodes */
  enableAutoRetry: boolean;

  /** Maximum number of retries per failed node */
  maxRetries: number;

  /** Delay between retries in milliseconds */
  retryDelayMs: number;

  /** Enable debug mode (more detailed logging) */
  debugMode: boolean;

  /** Log level: 'error' | 'warn' | 'info' | 'debug' */
  logLevel: 'error' | 'warn' | 'info' | 'debug';

  /** Enable execution history tracking */
  enableHistoryTracking: boolean;

  /** Number of execution history records to keep (0 = unlimited) */
  historyRetentionDays: number;

  /** Enable code formatting in node editors */
  enableCodeFormatting: boolean;

  /** Tab size for code editors */
  tabSize: number;

  /** DEBUG: Disable polling to test SignalR only */
  disablePollingForDebug: boolean;

  /**
   * When a HIL suspension event's `hilInboxReferences.inboxItemReferences` is empty (e.g. chat
   * nodes, which have Inbox disabled), read the envelope from the top-level
   * `hilInboxReferences.HilPresentationRequest` field instead of skipping the event.
   * See useExecutionSignalR's HIL suspension detection block. Turn off to revert to
   * inbox-items-only extraction (pre-chat-support behavior) if this fallback misbehaves.
   */
  enableHilTopLevelFallback: boolean;

  /** Node Template Repository URL — 'default' uses the built-in BizFirst endpoint */
  currentNodeTemplateRepositoryUrl: string;
}

interface SettingsState {
  // State
  settings: WorkflowSettings;

  // Actions
  updateSettings: (updates: Partial<WorkflowSettings>) => void;
  resetSettings: () => void;
}

// ============================================================================
// DEFAULTS
// ============================================================================

const DEFAULT_SETTINGS: WorkflowSettings = {
  autoSave: false,
  autoSaveInterval: 30, // seconds
  executionPollingInterval: 2000, // milliseconds
  showExecutionNotifications: true,
  nodeExecutionTimeout: 300, // seconds (5 minutes)
  enableAutoRetry: false,
  maxRetries: 3,
  retryDelayMs: 1000, // 1 second
  debugMode: false,
  logLevel: 'info',
  enableHistoryTracking: true,
  historyRetentionDays: 30,
  enableCodeFormatting: true,
  tabSize: 2,
  disablePollingForDebug: false, // Set to true to test SignalR only
  enableHilTopLevelFallback: true,
  currentNodeTemplateRepositoryUrl: 'default',
};

// ============================================================================
// ZUSTAND STORE
// ============================================================================

export const useSettingsStore = create<SettingsState>()(
  devtools(
    persist(
      (set) => ({
        // ====================================================================
        // INITIAL STATE
        // ====================================================================
        settings: DEFAULT_SETTINGS,

        // ====================================================================
        // ACTIONS
        // ====================================================================

        /**
         * Update one or more settings
         * Changes are persisted to localStorage automatically
         */
        updateSettings: (updates) => {
          set((state) => ({
            settings: { ...state.settings, ...updates },
          }));
        },

        /**
         * Reset all settings to defaults
         */
        resetSettings: () => {
          set({ settings: DEFAULT_SETTINGS });
        },
      }),
      {
        // Persist configuration
        name: 'workflow-settings',
        // Only persist the settings object, not the actions
        partialize: (state) => ({
          settings: state.settings,
        }),
        // Task 1191 — Flow Studio HIL Actions regression fix (2026-09-13):
        // zustand's `persist` default merge is a SHALLOW merge of top-level store keys
        // (`{ ...currentState, ...persistedState }`). `settings` is itself a single top-level
        // key holding a nested object, so the default merge REPLACES the entire `settings`
        // object with whatever was persisted — it does not fill in individual fields. Any
        // WorkflowSettings field added after a user's localStorage blob was last written (e.g.
        // `enableHilTopLevelFallback`, added 2026-08-16 in commit b3c25b011) is silently absent
        // from that stale blob, so `state.settings.enableHilTopLevelFallback` reads as
        // `undefined` — falsy — even though DEFAULT_SETTINGS says `true`.
        // Confirmed impact: `enableHilTopLevelFallback` gates the ONLY delivery path a chat-type
        // HIL suspension event has to the frontend (chat nodes run with Inbox disabled, so
        // InboxItemReferences is always empty — see useExecutionSignalR.ts's HIL suspension
        // detection block). With the flag reading falsy, that block hits its final `else` branch
        // and logs "envelope dropped" instead of pushing to HilEngine — the HIL Actions dock tab
        // then has nothing to show (`HilObservePanel` renders its "No active HIL event" empty
        // state) even though the backend suspended correctly and broadcast the event (confirmed
        // via live backend log: BroadcastProgressingAsync/ReceiveServerEvent fired normally).
        // Approval/Form HIL nodes are unaffected because they DO have real actor items, so they
        // never reach this fallback branch at all — this explains why the regression is
        // chat-specific, matching Binoy's report exactly.
        // Fix: merge the persisted `settings` object field-by-field against DEFAULT_SETTINGS
        // instead of replacing it wholesale, so any field missing from an older persisted blob
        // (this one or any future addition) always falls back to its current default rather than
        // silently becoming `undefined`. An explicit `false` a user actually set is still
        // honored — only genuinely MISSING keys fall back.
        merge: (persistedState, currentState) => {
          const persisted = persistedState as Partial<SettingsState> | undefined;
          return {
            ...currentState,
            ...persisted,
            settings: { ...DEFAULT_SETTINGS, ...persisted?.settings },
          };
        },
      }
    ),
    { name: 'SettingsStore' }
  )
);

// ============================================================================
// EXPORTS
// ============================================================================

export type { SettingsState };
export { DEFAULT_SETTINGS };
